import { useState } from "react";
import { Reveal } from "@components/ui";

export default function NewsletterSection() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [unitBuilding, setUnitBuilding] = useState("");
  const [preferredContent, setPreferredContent] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section className="relative w-full overflow-hidden bg-[#122d63]">
      {/* Decorative background shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-[200px] -right-[100px] w-[500px] h-[500px] rounded-full bg-[#1a3a7a] opacity-30" />
        <div className="absolute -bottom-[150px] -left-[80px] w-[400px] h-[400px] rounded-full bg-[#f03813] opacity-[0.07]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-white opacity-[0.02]" />
      </div>

      <div className="relative z-10 pt-12 md:pt-16 lg:pt-[100px] px-5 md:px-10 lg:px-[180px] pb-12 md:pb-16 lg:pb-[120px]">
        <div className="w-full max-w-[1368px] mx-auto flex flex-col gap-8 md:gap-12">
          {/* Header */}
          <Reveal>
            <div className="flex flex-col items-center text-center gap-3 md:gap-4">
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-white/80 text-[14px] md:text-[16px] font-medium tracking-[-0.04em] mb-2">
                Newsletter
              </span>
              <h2 className="text-[32px] md:text-[44px] lg:text-[60px] font-semibold text-white tracking-[-0.04em] leading-[1.1]">
                Stay Updated
              </h2>
              <p className="text-[18px] md:text-[22px] lg:text-[28px] text-white/60 tracking-[-0.04em]">
                Subscribe to our newsletter for the latest news and alerts
              </p>
            </div>
          </Reveal>

          {/* Form Card */}
          <Reveal>
            <div className="rounded-[24px] bg-white p-6 md:p-10 lg:p-16 shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
              {!submitted ? (
                <form onSubmit={handleSubmit} className="flex flex-col gap-6 md:gap-8">
                  {/* Row 1: Full Name + Email */}
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex flex-col gap-2 flex-1">
                      <label className="text-[15px] md:text-[17px] font-medium text-[#090d2c] tracking-[-0.02em]">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter your full name"
                        required
                        className="h-[52px] md:h-[56px] rounded-[14px] border-2 border-[#e8e9ed] px-5 text-[16px] md:text-[18px] text-[#090d2c] placeholder:text-[#b9bbc6] outline-none focus:border-[#122d63] focus:shadow-[0_0_0_3px_rgba(18,45,99,0.1)] transition-all"
                      />
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                      <label className="text-[15px] md:text-[17px] font-medium text-[#090d2c] tracking-[-0.02em]">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        required
                        className="h-[52px] md:h-[56px] rounded-[14px] border-2 border-[#e8e9ed] px-5 text-[16px] md:text-[18px] text-[#090d2c] placeholder:text-[#b9bbc6] outline-none focus:border-[#122d63] focus:shadow-[0_0_0_3px_rgba(18,45,99,0.1)] transition-all"
                      />
                    </div>
                  </div>

                  {/* Row 2: Unit/Building */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[15px] md:text-[17px] font-medium text-[#090d2c] tracking-[-0.02em]">
                      Unit/Building{" "}
                      <span className="text-[#b9bbc6] font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={unitBuilding}
                      onChange={(e) => setUnitBuilding(e.target.value)}
                      placeholder="e.g. Building A, Unit 201"
                      className="h-[52px] md:h-[56px] rounded-[14px] border-2 border-[#e8e9ed] px-5 text-[16px] md:text-[18px] text-[#090d2c] placeholder:text-[#b9bbc6] outline-none focus:border-[#122d63] focus:shadow-[0_0_0_3px_rgba(18,45,99,0.1)] transition-all"
                    />
                  </div>

                  {/* Row 3: Preferred Content */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[15px] md:text-[17px] font-medium text-[#090d2c] tracking-[-0.02em]">
                      Preferred Content
                    </label>
                    <div className="relative">
                      <select
                        value={preferredContent}
                        onChange={(e) => setPreferredContent(e.target.value)}
                        className="w-full h-[52px] md:h-[56px] rounded-[14px] border-2 border-[#e8e9ed] px-5 pr-12 text-[16px] md:text-[18px] text-[#090d2c] outline-none focus:border-[#122d63] focus:shadow-[0_0_0_3px_rgba(18,45,99,0.1)] transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="" disabled>
                          Select your preferred content
                        </option>
                        <option value="all">All Updates</option>
                        <option value="safety">Safety Alerts</option>
                        <option value="tariff">Tariff & Billing Updates</option>
                        <option value="service">Service Announcements</option>
                        <option value="maintenance">Maintenance Notices</option>
                        <option value="community">Community & CSR</option>
                        <option value="corporate">Corporate News</option>
                      </select>
                      <svg
                        className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#62636c]"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="flex justify-center pt-2 md:pt-4">
                    <button
                      type="submit"
                      className="group flex items-center gap-3 bg-[#090d2c] hover:bg-[#122d63] text-white rounded-full py-3.5 pl-7 pr-3 shadow-[0_4px_16px_rgba(9,13,44,0.3)] hover:shadow-[0_6px_24px_rgba(9,13,44,0.4)] transition-all duration-300"
                    >
                      <span className="text-[17px] md:text-[20px] font-medium tracking-[-0.02em]">
                        Subscribe Now
                      </span>
                      <span className="flex items-center justify-center w-[40px] h-[40px] rounded-full bg-[#f03813] group-hover:bg-[#ff4d2b] transition-colors duration-300">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="transition-transform duration-300 group-hover:translate-x-0.5"
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </span>
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex flex-col items-center py-8 md:py-12 gap-4 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#1a7a4f]/10 flex items-center justify-center">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1a7a4f"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 className="text-[24px] md:text-[28px] font-semibold text-[#090d2c] tracking-[-0.04em]">
                    You're subscribed!
                  </h3>
                  <p className="text-[16px] md:text-[18px] text-[#62636c] max-w-[400px]">
                    Thank you for subscribing. We'll keep you updated with the latest news and alerts.
                  </p>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
